import { useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { onPositionMemoryExpire } from "@/lib/positionMemory";

// Resume playback for feed / profile / theater videos (sermons): the position is stored
// on the ACCOUNT (PUT /videos/progress) so a member resumes on any device. A module cache
// makes the resume instant within the session (feed → viewer → back) before any refetch.
// Le serveur n'en renvoie que les positions plus récentes que le délai de mémorisation (admin).
export const RESUME_MIN_SECONDS = 3;
export const RESUME_END_MARGIN = 5;
const SAVE_EVERY_MS = 5000;
const cache = new Map(); // key → { t, d, at }
onPositionMemoryExpire(() => cache.clear());

export const resumeEnabled = (config) => config?.settings?.video_resume_enabled !== false;

// Seed the cache from a post payload (`resume_t`) — never downgrades a fresher local value.
export const primeResume = (key, t) => {
  if (!key || typeof t !== "number" || t <= 0) return;
  const cur = cache.get(key);
  if (!cur || (Date.now() - cur.at) > 60_000) cache.set(key, { t, d: cur?.d, at: 0 });
};

export const getResume = (key) => (key && cache.get(key)?.t) || 0;

export const fetchResume = async (keys) => {
  const list = (Array.isArray(keys) ? keys : [keys]).filter(Boolean);
  if (!list.length) return {};
  try {
    const { data } = await api.get("/videos/progress", { params: { keys: list.join(",") } });
    Object.entries(data?.progress || {}).forEach(([k, v]) => cache.set(k, { t: Number(v.t) || 0, d: v.d || null, at: 0 }));
    return data?.progress || {};
  } catch { return {}; }
};

// Last 5 s (or last 10 % of a short clip) = finished → next play starts from the beginning.
export const resumeFinished = (t, d) => d > 0 && t >= d - Math.min(RESUME_END_MARGIN, d * 0.1);
const finished = resumeFinished;

// Remember `t` for `key` (cache now, server throttled unless `flush`).
const pending = new Map(); // key → timeout id
const latest = new Map();  // key → { t, d } (what the next server write sends)
export const saveProgress = (key, t, d, flush = false) => {
  if (!key || typeof t !== "number" || !isFinite(t)) return;
  const dur = isFinite(d) && d > 0 ? d : null;
  if (t < RESUME_MIN_SECONDS || finished(t, dur)) cache.delete(key); else cache.set(key, { t, d: dur, at: Date.now() });
  latest.set(key, { t, d: dur });
  const send = () => {
    pending.delete(key);
    const v = latest.get(key);
    if (v) api.put("/videos/progress", { key, t: v.t, d: v.d }).catch(() => {});
  };
  if (flush) { clearTimeout(pending.get(key)); send(); return; }
  if (!pending.has(key)) pending.set(key, setTimeout(send, SAVE_EVERY_MS));
};

// Attach resume + progress saving to a <video>/<audio> element ref.
// `enabled` false → nothing happens (admin switch off, muted feed teaser, guests).
// `onSave(t, d)` lets the caller mirror the position (feed progress bar).
export function useVideoResume(ref, { key, enabled = true, initial = 0, onSave } = {}) {
  const seekedRef = useRef(false);
  const lastSave = useRef(0);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => { seekedRef.current = false; primeResume(key, initial); }, [key, initial]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !key || !enabled) return undefined;
    const trySeek = () => {
      if (seekedRef.current) return;
      const d = isFinite(el.duration) && el.duration > 0 ? el.duration : 0;
      if (!d) return;
      seekedRef.current = true;
      const t = getResume(key);
      if (t >= RESUME_MIN_SECONDS && !finished(t, d) && Math.abs(el.currentTime - t) > 1) {
        try { el.currentTime = t; } catch { /* not seekable yet */ }
      }
    };
    const record = (flush) => {
      const t = el.currentTime;
      if (!(t > 0)) return;
      saveProgress(key, t, el.duration, flush);
      onSaveRef.current?.(t, el.duration);
    };
    const onTime = () => {
      const now = Date.now();
      if (now - lastSave.current < SAVE_EVERY_MS) return;
      lastSave.current = now;
      record(false);
    };
    const onPause = () => record(true);
    if (el.readyState >= 1) trySeek();
    el.addEventListener("loadedmetadata", trySeek);
    el.addEventListener("durationchange", trySeek);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onPause);
    return () => {
      el.removeEventListener("loadedmetadata", trySeek);
      el.removeEventListener("durationchange", trySeek);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onPause);
      record(true);
      seekedRef.current = false; // re-enabled later (sound back on) → seek again to the saved spot
    };
  }, [ref, key, enabled]);
}
